import {
  Box,
  Card,
  CardBody,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  CardHeader,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Icon,
  Tbody,
  Td,
  TableContainer,
  Avatar,
  Textarea,
  Divider,
  CardFooter,
  color,
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import {
  FaArrowDown,
  FaArrowUp,
  FaDollarSign,
  FaFileWord,
  FaPercentage,
  FaStickyNote,
  FaThumbsDown,
  FaThumbsUp,
} from 'react-icons/fa';
import ReactApexChart from 'react-apexcharts';
import BenchmarkOWStat from './benchOverviewStat';
import ReskinnedBenchmarkOverview from './ReskinnedBenchmarkOverview';

import CustomDateRangePicker from '../../utility/dateRangePicker';
import LocationDropDown from '../../utility/location';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { useFeatureFlagFromContext } from '../../../launchdarkly';

const BenchmarkOW = props => {
  // All hooks must be called at the top level
  const showReskin = useFeatureFlagFromContext('showReskin', false);
  const [overview, setOverview] = useState({});

  // This function will be used in the non-reskinned version
  // It's defined here to avoid ESLint errors with React Hooks
  // eslint-disable-next-line no-unused-vars
  const handleDate = value => {
    if (showReskin) return; // Skip if using reskinned version

    const fromDate =
      (value[0].getMonth() > 8
        ? value[0].getMonth() + 1
        : '0' + (value[0].getMonth() + 1)) +
      '-' +
      (value[0].getDate() > 9 ? value[0].getDate() : '0' + value[0].getDate()) +
      '-' +
      value[0].getFullYear();

    const toDate =
      (value[1].getMonth() > 8
        ? value[1].getMonth() + 1
        : '0' + (value[1].getMonth() + 1)) +
      '-' +
      (value[1].getDate() > 9 ? value[1].getDate() : '0' + value[1].getDate()) +
      '-' +
      value[1].getFullYear();

    const formData = new FormData();
    formData.append('fromDate', fromDate);
    formData.append('toDate', toDate);

    fetchWithAuth('/api/benchmark_data/', {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          setOverview(data['overview']);
        } else {
          alert('Session Expired!.');
          window.open('/');
        }
      })
      .catch(err => console.error(err));
  };

  // All hooks must be called before any conditional returns
  useEffect(() => {
    // Only fetch data if not using reskinned version
    if (!showReskin) {
      fetchWithAuth('/api/benchmark_data/', {
        method: 'POST',
      })
        .then(response => response.json())
        .then(data => {
          if (data.code === undefined) {
            setOverview(data['overview']);
          } else {
            window.open('/', '_self');
            alert('Session Expired!.');
          }
        })
        .catch(err => {
          console.error(err);
        });
    }
  }, [showReskin]); // Include showReskin in the dependency array

  // If showReskin feature flag is enabled, render the reskinned version
  if (showReskin) {
    return <ReskinnedBenchmarkOverview {...props} />;
  }

  return (
    <Card width={'100%'}>
      <CardHeader
        height={{ base: '150px', sm: '100px', md: '70px', lg: '70px' }}
      >
        <Flex
          gap={2}
          height="100%"
          direction={{ base: 'column', sm: 'row' }}
          justifyContent={{
            base: 'space-between',
            sm: 'space-around',
            md: 'space-between',
          }}
        >
          <Flex gap={2} alignItems={'center'}>
            <Icon as={FaPercentage} />
            <Text fontSize={'md'}>Overview</Text>
          </Flex>
          <Flex
            gap={2}
            direction={{ base: 'column', sm: 'column', md: 'row' }}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Flex
              flex={1}
              minWidth={{ base: '280px', sm: '280px', md: '250px' }}
              maxWidth={{ base: '280px', sm: '280px', md: '250px' }}
            >
              <LocationDropDown
                locationValue={props.locationValue}
                setLocation={props.setLocation}
              />
            </Flex>
            <Flex
              flex={1}
              fontSize={'sm'}
              width={'100%'}
              justifyContent={{
                base: 'center',
                sm: 'space-between',
                md: 'space-between',
              }}
            >
              <CustomDateRangePicker
                dateValue={props.handleDate}
                value={props.value}
              />
            </Flex>
          </Flex>
        </Flex>
      </CardHeader>
      <Divider mt={0} />
      <CardBody>
        <Flex
          gap={4}
          justifyContent={'center'}
          width={'100%'}
          flexWrap={'wrap'}
        >
          <Flex flex={1}>
            <Flex width={'100%'}>
              <BenchmarkOWStat
                header="Average Margins"
                num={
                  props.overview !== undefined
                    ? props.overview.avg_cost + '%'
                    : ''
                }
                denom={
                  props.overview !== undefined
                    ? props.overview.avg_inc + '%'
                    : ''
                }
                numDesc="Cost"
                denomDesc="Net Income"
              />
            </Flex>
          </Flex>
          <Flex flex={1}>
            <Flex width={'100%'}>
              <BenchmarkOWStat
                header="Best in Class"
                num={
                  props.overview !== undefined
                    ? props.overview.bic_cost + '%'
                    : ''
                }
                denom={
                  props.overview !== undefined
                    ? props.overview.bic_inc + '%'
                    : ''
                }
                numDesc="Cost"
                denomDesc="Net Income"
              />
            </Flex>
          </Flex>
          <Flex flex={1}>
            <Flex width={'100%'}>
              <BenchmarkOWStat
                header="Your Margins"
                num={
                  props.overview !== undefined
                    ? props.overview.exp_rev + '%'
                    : ''
                }
                denom={
                  props.overview !== undefined
                    ? props.overview.inc_rev + '%'
                    : ''
                }
                numDesc="Cost"
                denomDesc="Net Income"
                footer=""
                bgColor="#fae3a0"
              />
            </Flex>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default BenchmarkOW;
